/**
 * CountReconciliationInfo — ITT-4.
 *
 * Three different counts surface across the Report Insights surfaces:
 *
 *   - Site Evidence banner: N of M high-severity conflicts (SED-level
 *     provider-vs-doc disagreements).
 *   - Quality tab: K issues flagged (AI-review pipeline across
 *     Accuracy / Citations / Completeness).
 *   - Findings tab: P sanity findings (post-gen final review across
 *     the report narrative).
 *
 * The three numbers measure different things and may overlap (a single
 * underlying problem can surface in 2-3 of them). This component
 * surfaces that relationship as a small hover/focus tooltip so users
 * stop trying to reconcile "14 + 4 = 18".
 *
 * Pure presentation — the copy is hard-coded so this can mount in any
 * of the three surfaces without prop-drilling. The data-testid
 * (`count-reconciliation-info`) lets all three surfaces share the same
 * audit hook.
 */
import React, { useState } from 'react';
import { Info } from 'lucide-react';

interface Props {
  /** Surface name — used in the trigger's aria-label so screen readers
   *  can disambiguate the three identical info icons. */
  surface: 'site-evidence' | 'quality' | 'findings';
}

const SURFACE_LABEL: Record<Props['surface'], string> = {
  'site-evidence': 'site-evidence conflicts count',
  'quality': 'quality-issues count',
  'findings': 'sanity-findings count',
};

const TOOLTIP_COPY = [
  'The three counts measure different things:',
  '• Site Evidence conflicts — provider data vs. uploaded doc disagreements (SED-level).',
  '• Quality issues flagged — AI Review pipeline issues across Accuracy / Citations / Completeness.',
  '• Sanity findings — post-gen final review across the report narrative.',
  'These three sets may overlap — a single underlying problem can surface in two or three of them.',
].join('\n');

export function CountReconciliationInfo({ surface }: Props) {
  // Local controlled state — we toggle on click and also flip on
  // hover/focus via CSS (group-hover/group-focus-within). The click
  // path is the keyboard-only path (Enter on the button).
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center group">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={`What this number means — ${SURFACE_LABEL[surface]}`}
        aria-expanded={open}
        className="ml-1 inline-flex items-center text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        data-testid="count-reconciliation-info"
        data-surface={surface}
      >
        <Info className="w-3 h-3" aria-hidden="true" />
      </button>
      <span
        role="tooltip"
        className={`pointer-events-none absolute left-0 top-full mt-1 z-30 w-72 text-[10px] text-text-muted leading-relaxed bg-popover border border-border p-2 shadow-md whitespace-pre-line ${
          open ? 'block' : 'hidden group-hover:block group-focus-within:block'
        }`}
        data-testid="count-reconciliation-tooltip"
        data-surface={surface}
      >
        {TOOLTIP_COPY}
      </span>
    </span>
  );
}
